/*
 * The header quota strip.
 *
 * Every gauge names the layer that produced its ceiling, because the two-layer
 * quota model is the reason a number is actionable: `min(plan, rc)` with an
 * optional per-base override on top, so hitting a wall is an upgrade, an operator
 * conversation, or neither, and the gauge is the only place that distinction can
 * be read.
 *
 * The rate numerators come from the response's own `rate_usage` block — the same
 * rolling-window counters the server's quota checks read, so the gauge cannot
 * drift from enforcement. When the response carries none the gauge says so
 * instead of rendering a plausible zero.
 */
import type { KnowledgeBaseLimits } from '../../../api/knowledgeBases.ts'
import { bytes, num, percent } from '../lib/format.ts'
import { isPressing, sourceLook } from '../lib/limits.ts'

export interface RateCounts {
  ingestsToday: number | null
  opsThisMonth: number | null
}

interface QuotaStripProps {
  limits: KnowledgeBaseLimits
  rates: RateCounts
}

interface GaugeSpec {
  key: string
  label: string
  used: number | null
  unit: 'count' | 'bytes'
  colour: string
}

const RAW_COLOUR = '#6d9ad4'
const WIKI_COLOUR = '#c2643f'
const RATE_COLOUR = '#e0a23f'

export function QuotaStrip({ limits, rates }: QuotaStripProps) {
  const usage = limits.usage
  const gauges: GaugeSpec[] = [
    {
      key: 'kb_max_raw_files',
      label: 'raw · файлы',
      used: usage.raw_files ?? 0,
      unit: 'count',
      colour: RAW_COLOUR,
    },
    {
      key: 'kb_max_raw_bytes',
      label: 'raw · вес',
      used: usage.raw_bytes ?? 0,
      unit: 'bytes',
      colour: RAW_COLOUR,
    },
    {
      key: 'kb_max_wiki_pages',
      label: 'wiki · страницы',
      used: usage.wiki_pages ?? 0,
      unit: 'count',
      colour: WIKI_COLOUR,
    },
    {
      key: 'kb_max_wiki_bytes',
      label: 'wiki · вес',
      used: usage.wiki_bytes ?? 0,
      unit: 'bytes',
      colour: WIKI_COLOUR,
    },
    {
      key: 'kb_ingests_per_day',
      label: 'ingest сегодня',
      used: rates.ingestsToday,
      unit: 'count',
      colour: RATE_COLOUR,
    },
    {
      key: 'kb_ops_per_month',
      label: 'операций за месяц',
      used: rates.opsThisMonth,
      unit: 'count',
      colour: RATE_COLOUR,
    },
  ]

  return (
    <div className="qstrip">
      {gauges.map((gauge) => {
        const limit = limits.limits?.[gauge.key]
        if (!limit) return null
        return <Gauge key={gauge.key} spec={gauge} limit={limit} />
      })}
    </div>
  )
}

function Gauge({
  spec,
  limit,
}: {
  spec: GaugeSpec
  limit: NonNullable<KnowledgeBaseLimits['limits']>[string]
}) {
  const format = spec.unit === 'bytes' ? bytes : num
  // `null` is unbounded; `0` is a real limit meaning "none allowed". Testing for
  // falsiness would render an account that may store nothing as unlimited.
  const unbounded = limit.value === null || limit.value === undefined
  const look = sourceLook(limit.source)
  const ratio = spec.used === null || unbounded ? null : percent(spec.used, limit.value)
  const pressing = spec.used !== null && isPressing(spec.used, limit.value)

  return (
    <div className="qs">
      <div className="qs__lab">
        <span>{spec.label}</span>
        <b style={pressing ? { color: 'var(--wa-tx)' } : undefined}>
          {spec.used === null ? '—' : format(spec.used)}
          {unbounded ? '' : ` / ${format(limit.value as number)}`}
        </b>
      </div>
      <div className="meter">
        <i
          style={{
            width: `${ratio ?? (unbounded ? 100 : 0)}%`,
            background: unbounded ? 'rgba(255,255,255,.14)' : spec.colour,
          }}
        />
      </div>
      <div className="qs__src">
        {spec.used === null ? (
          <span title="API не отдаёт счётчик для этого лимита">счётчик недоступен</span>
        ) : null}
        <span className={`src-tag ${look.className}`}>{look.label}</span>
      </div>
    </div>
  )
}
